import React, { useState, useEffect, useRef } from 'react';
import { Camera, MapPin, CheckCircle, AlertCircle, Users, Clock, Fingerprint, Trash2, X, Smartphone, Calendar, Shield } from 'lucide-react';
import { api, getApiBase } from '../utils/api';
import { getCurrentLocation, getCameraConstraints } from '../utils/helpers';
import Swal from 'sweetalert2';
import * as faceapi from 'face-api.js';

import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from 'capacitor-native-biometric';

// Helper functions for WebAuthn
const base64urlToBuffer = (base64url) => {
  const padding = '='.repeat((4 - base64url.length % 4) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
};

const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let charCode of bytes) {
    str += String.fromCharCode(charCode);
  }
  return window.btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const FaceScanner = ({ user, appUpdateStatus = 'ok' }) => {
  // Refs สำหรับ Passkey & Camera
  const abortControllerRef = useRef(null);
  const videoRef = useRef(null);
  const captureCanvasRef = useRef(null);
  const streamRef = useRef(null);
  const boxRef = useRef(null);

  // States ทั่วไป
  const [isScanning, setIsScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [location, setLocation] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date()); 
  const [wfhMode, setWfhMode] = useState(false);
  const [isWfhChecked, setIsWfhChecked] = useState(false);
  
  // States สำหรับจัดการ Passkey Modal
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [devices, setDevices] = useState([]); 
  
  // State สลับระบบ
  const [useFallbackCamera, setUseFallbackCamera] = useState(false);
  
  // [เพิ่ม] ตัวแปรคุมโหมดกล้อง: ถ้าเป็น WFH หรือกดสำรอง ให้เป็นโหมดกล้องทันที
  const isCameraMode = wfhMode || useFallbackCamera; 

  // States สำหรับ Camera (Fallback & WFH)
  const [cameraActive, setCameraActive] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false); 
  const [readyCountdown, setReadyCountdown] = useState(null); 
  const [scanMessage, setScanMessage] = useState(null);

  const maxDevices = 2;
  const boundEmployeeId = localStorage.getItem('device_bound_to_employee');
  const isCurrentDeviceBound = boundEmployeeId === user?.employee_id;
  const isBoundToAnotherUser = boundEmployeeId && boundEmployeeId !== user?.employee_id;

  const [realPasskeyCount, setRealPasskeyCount] = useState(
    user?.passkey_count !== undefined ? user.passkey_count : (user?.has_passkey ? 1 : 0)
  );
  
  const [isRegistered, setIsRegistered] = useState(() => isCurrentDeviceBound);
  const [hasAutoTriggered, setHasAutoTriggered] = useState(false);

  const isWrongDevice = isBoundToAnotherUser || (!isCurrentDeviceBound && realPasskeyCount >= maxDevices);

  // 1. โหลด Face-api Models ล่วงหน้าเตรียมไว้เผื่อต้องใช้กล้อง
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = 'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights';
        await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
        setModelsLoaded(true);
      } catch (error) {
        console.error("Error loading face-api models:", error);
      }
    };
    loadModels();
  }, []);

  // 2. โหลด Passkeys
  useEffect(() => {
    if (!user?.employee_id) return;
    const fetchFreshPasskeys = async () => {
      try {
        const response = await api.getPasskeys(user.employee_id);
        if (response && response.success) {
          const fetchedDevices = response.devices || [];
          setDevices(fetchedDevices);
          setRealPasskeyCount(fetchedDevices.length);
        }
      } catch (error) {
        console.error('Error fetching passkeys count:', error);
      }
    };
    fetchFreshPasskeys();
  }, [user]);

  // 3. จัดการเวลา
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 4. ระบบนับถอยหลังสำหรับกล้องสำรอง
  useEffect(() => {
    if (readyCountdown === null || readyCountdown <= 0) return;
    const timer = setTimeout(() => { setReadyCountdown(prev => prev - 1); }, 1000);
    return () => clearTimeout(timer);
  }, [readyCountdown]);

  // แจ้งให้ระบบทราบทันทีเมื่อเข้ามาที่หน้าลงเวลา
  useEffect(() => {
    if (user?.employee_id) {
      const token = localStorage.getItem('auth_token') || ''; 
      const url = typeof getApiBase === 'function' ? `${getApiBase()}attendance/log-visit` : 'https://smartface.edusoft.top/api/attendance/log-visit';

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ employee_id: user.employee_id })
      })
      .then(res => res.json())
      .then(data => console.log("Visit Logged:", data)) 
      .catch(err => console.error('Log visit error:', err));
    }
  }, [user]);

  // 5. ระบบเช็ค Location และ WFH
  const checkWfhMode = async () => {
    try {
      const data = await api.getOrganizations();
      if (data.length > 0 && data[0].allow_wfh == 1) {
        setWfhMode(true);
      } else {
        setWfhMode(false);
      }
    } catch (error) {
      console.error('Error checking WFH mode:', error);
    } finally {
      setIsWfhChecked(true); 
    }
  };

  const loadCurrentLocation = async () => {
    setLocation({ loading: true });
    try {
      const locationData = await getCurrentLocation();
      setLocation({ ...locationData, loading: false });
    } catch (error) {
      setLocation({ loading: false, error: true, message: 'ไม่สามารถรับตำแหน่งได้ กรุณาเปิด GPS' });
    }
  };

  useEffect(() => {
    loadCurrentLocation();
    checkWfhMode();
  }, []);

  // 6. จัดการกล้อง (เปิดเมื่อเป็นโหมด WFH หรือกดสำรอง)
  useEffect(() => {
    if (isCameraMode && modelsLoaded && !cameraActive && !result) {
      startCamera();
    } else if (!isCameraMode && cameraActive) {
      stopCamera();
    }
  }, [isCameraMode, modelsLoaded, cameraActive, result]);

  // 7. ระบบเป้าหมายหลัก: ออโต้รัน Passkey ทันทีที่โหลดตำแหน่งเสร็จ (ข้ามถ้าเป็นโหมดกล้อง)
  useEffect(() => {
    if (appUpdateStatus === 'checking' || appUpdateStatus === 'required') return;

    if (isRegistered && !hasAutoTriggered && !isScanning && !result && isWfhChecked && !isCameraMode) {
      const isLocationReady = location && !location.loading && !location.error && location.accuracy <= 100 && !location.skipped && !location.manual && location.latitude !== 0;
      
      if (isLocationReady) {
        setHasAutoTriggered(true);
        handleWebAuthnScan(true); 
      }
    }
  }, [isRegistered, hasAutoTriggered, location, isScanning, result, isWfhChecked, appUpdateStatus, isCameraMode]);

  // เคลียร์กล้องตอนออก
  useEffect(() => {
    return () => stopCamera();
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(getCameraConstraints ? getCameraConstraints() : { video: { facingMode: 'user' } });
      streamRef.current = stream;
      if (!videoRef.current) return;
      videoRef.current.srcObject = stream;
      setCameraActive(true);
      setScanMessage(null);
    } catch (error) {
      Swal.fire('ข้อผิดพลาด', 'ไม่สามารถเข้าถึงกล้องได้', 'error');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  };

  const handleRegisterDevice = async () => {
    try {
      setIsScanning(true);
      if (Capacitor.isNativePlatform()) {
        const check = await NativeBiometric.isAvailable();
        if (!check.isAvailable) throw new Error('DEVICE_NOT_SECURE');
        try { await NativeBiometric.verifyIdentity({ reason: "ยืนยันตัวตนเพื่อลงทะเบียนอุปกรณ์นี้", title: "ลงทะเบียนอุปกรณ์" }); } 
        catch (e) { throw new Error('USER_CANCELLED'); }

        await api.savePasskey({
          employee_id: user.employee_id, credential_id: 'native_device_' + user.employee_id + '_' + Date.now(),
          public_key: 'native_auth_bypass', client_data: 'capacitor_app'
        });
      } else {
        if (!window.PublicKeyCredential) throw new Error('BROWSER_NOT_SUPPORTED');
        const options = await api.getWebAuthnRegistrationOptions(user.employee_id);
        options.challenge = base64urlToBuffer(options.challenge);
        options.user.id = base64urlToBuffer(options.user.id);

        const credential = await navigator.credentials.create({ publicKey: options });
        await api.savePasskey({
          employee_id: user.employee_id, credential_id: credential.id,
          public_key: bufferToBase64url(credential.response.attestationObject), 
          client_data: bufferToBase64url(credential.response.clientDataJSON)
        });
      }

      localStorage.setItem('device_bound_to_employee', user.employee_id);
      setIsRegistered(true);
      setHasAutoTriggered(false); 
      Swal.fire({ title: 'ลงทะเบียนสำเร็จ!', text: 'อุปกรณ์นี้ผูกกับบัญชีของคุณแล้ว สามารถสแกนเพื่อลงเวลาได้ทันที', icon: 'success', confirmButtonColor: '#3B82F6', customClass: { popup: 'rounded-2xl', confirmButton: 'rounded-xl px-6 py-2 font-bold' }});
    } catch (error) {
      let errorTitle = 'การลงทะเบียนล้มเหลว';
      let errorMessage = 'เกิดข้อผิดพลาดในการลงทะเบียน กรุณาลองใหม่อีกครั้ง';
      if (error.message === 'DEVICE_NOT_SECURE') errorMessage = 'อุปกรณ์ของคุณยังไม่ได้ตั้งค่ารหัสล็อคหน้าจอ หรือไม่ได้บันทึกลายนิ้วมือ/ใบหน้าไว้';
      else if (error.message === 'USER_CANCELLED' || error.name === 'NotAllowedError') errorMessage = 'คุณยกเลิกการทำรายการ หรือไม่อนุญาตให้สแกน';
      else if (error.message === 'BROWSER_NOT_SUPPORTED') errorMessage = 'อุปกรณ์หรือบราวเซอร์ของคุณไม่รองรับระบบ Passkey';
      Swal.fire({ title: errorTitle, text: errorMessage, icon: 'error', confirmButtonColor: '#EF4444' });
    } finally {
      setIsScanning(false);
    }
  };

  const handleDeleteDevice = async (id, deviceName) => {
    const confirm = await Swal.fire({ title: 'ลบอุปกรณ์?', text: `คุณต้องการลบอุปกรณ์ "${deviceName}" ใช่หรือไม่?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#ef4444', cancelButtonColor: '#6b7280', confirmButtonText: 'ลบข้อมูล', cancelButtonText: 'ยกเลิก' });
    if (confirm.isConfirmed) {
      try {
        setIsScanning(true); 
        const response = await api.deletePasskey(id);
        if (response.success) {
          const updatedDevices = devices.filter(d => d.id !== id);
          setDevices(updatedDevices);
          setRealPasskeyCount(updatedDevices.length);
          if (updatedDevices.length < maxDevices) setShowDeviceModal(false); 
          Swal.fire('ลบแล้ว', 'ลบอุปกรณ์เรียบร้อยแล้ว', 'success');
        }
      } catch (error) {
        Swal.fire('ผิดพลาด', 'ไม่สามารถลบได้: ' + error.message, 'error');
      } finally {
        setIsScanning(false);
      }
    }
  };

  const validateLocation = () => {
    if (!wfhMode) {
      if (!location || location.loading) { Swal.fire('รอดำเนินการ', 'กรุณารอระบบหาตำแหน่งของคุณให้เสร็จก่อน', 'info'); if (!location) loadCurrentLocation(); return false; }
      if (location.error) { Swal.fire({ title: 'เกิดข้อผิดพลาด', html: 'ไม่สามารถรับตำแหน่ง GPS ได้<br>กรุณา: เปิด GPS และอนุญาตให้เบราว์เซอร์เข้าถึงตำแหน่ง', icon: 'error' }); loadCurrentLocation(); return false; }
      if (location.skipped || location.manual || location.latitude === 0) { Swal.fire('แจ้งเตือน', 'ระบบต้องการตำแหน่งที่แม่นยำเพื่อบันทึกเวลา กรุณาเปิด GPS', 'warning'); loadCurrentLocation(); return false; }
      if (location.accuracy && location.accuracy > 100) { Swal.fire({ title: 'ตำแหน่งไม่แม่นยำ', html: `ความแม่นยำของตำแหน่งต่ำเกินไป (±${Math.round(location.accuracy)}m)<br><br>กรุณา: ไปที่บริเวณโล่ง แล้วลองอีกครั้ง`, icon: 'warning' }); return false; }
    }
    return true;
  };

  // ---------------------------------------------------------
  // ระบบ Passkey (ระบบหลัก)
  // ---------------------------------------------------------
  const handleWebAuthnScan = async (isAuto = false) => {
    if (!validateLocation()) return;

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      setIsScanning(true);

      if (Capacitor.isNativePlatform()) {
        const check = await NativeBiometric.isAvailable();
        if (!check.isAvailable) throw new Error('DEVICE_NOT_SECURE');
        try { await NativeBiometric.verifyIdentity({ reason: "สแกนนิ้ว/ใบหน้า เพื่อลงเวลาเข้า-ออกงาน", title: "ลงเวลาทำงาน" }); } 
        catch (e) { throw new Error('USER_CANCELLED'); }
      } else {
        if (!window.PublicKeyCredential) throw new Error('BROWSER_NOT_SUPPORTED');
        const options = await api.getWebAuthnAuthOptions(user.employee_id);
        options.challenge = base64urlToBuffer(options.challenge);
        if (options.allowCredentials) options.allowCredentials.forEach(c => c.id = base64urlToBuffer(c.id));
        
        await navigator.credentials.get({ publicKey: options, signal: abortControllerRef.current.signal });
      }

      const requestData = {
        face_descriptor: [], 
        location: wfhMode ? { wfh_mode: true, latitude: 0, longitude: 0 } : location,
        employee_id: user.employee_id, confirmed: true, captured_image: null 
      };
      
      const data = await api.recognizeFace(requestData);
      setResult(data);
      setIsScanning(false); 
      
    } catch (error) {
      console.log('Passkey error/cancelled:', error.message || error.name);
      setIsScanning(false);
      
      if (isAuto && (error.name === 'NotAllowedError' || error.name === 'SecurityError' || error.message === 'USER_CANCELLED')) {
         return; 
      }
      setUseFallbackCamera(true); 
    }
  };

  // ---------------------------------------------------------
  // ระบบ Camera (ระบบสำรอง Fallback & WFH)
  // ---------------------------------------------------------
  const captureImage = () => {
    const video = videoRef.current;
    const captureCanvas = captureCanvasRef.current;
    if (!video || !captureCanvas) return null;
    captureCanvas.width = video.videoWidth;
    captureCanvas.height = video.videoHeight;
    const ctx = captureCanvas.getContext('2d');
    ctx.save();
    ctx.scale(-1, 1);
    ctx.drawImage(video, -captureCanvas.width, 0, captureCanvas.width, captureCanvas.height);
    ctx.restore();
    return captureCanvas.toDataURL('image/jpeg', 0.8);
  };

  const handleManualCapture = async () => {
    if (!validateLocation()) return;

    const video = videoRef.current;
    const box = boxRef.current;
    if (!video || !box) return;

    setIsDetecting(true); 
    setScanMessage(null);

    try {
      const detections = await faceapi.detectAllFaces(video, new faceapi.TinyFaceDetectorOptions());
      if (detections.length === 0) {
        setScanMessage('ไม่พบใบหน้า กรุณามองกล้อง!');
        setTimeout(() => setScanMessage(null), 3000);
        setIsDetecting(false);
        return; 
      }

      const videoRect = video.getBoundingClientRect();
      const boxRect = box.getBoundingClientRect();
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;

      const videoRatio = videoWidth / videoHeight;
      const elementRatio = videoRect.width / videoRect.height;

      let renderWidth, renderHeight, offsetX = 0, offsetY = 0;
      if (elementRatio > videoRatio) {
        renderWidth = videoRect.width;
        renderHeight = renderWidth / videoRatio;
        offsetY = 0; // Center top เพราะเราใช้ objectPosition: 'center top'
      } else {
        renderHeight = videoRect.height;
        renderWidth = renderHeight * videoRatio;
        offsetX = (renderWidth - videoRect.width) / 2;
      }

      const scale = videoWidth / renderWidth;
      const margin = 20; 
      const boxLeft = ((boxRect.left - videoRect.left + offsetX) * scale) - margin;
      const boxRight = ((boxRect.right - videoRect.left + offsetX) * scale) + margin;
      const boxTop = ((boxRect.top - videoRect.top + offsetY) * scale) - margin;
      const boxBottom = ((boxRect.bottom - videoRect.top + offsetY) * scale) + margin;

      const face = detections[0].box;
      const faceLeft = face.x;
      const faceRight = face.x + face.width;
      const faceTop = face.y;
      const faceBottom = face.y + face.height;

      // เช็คให้ขอบหน้าทั้ง 4 ด้านต้องอยู่ข้างในกรอบทั้งหมด
      const isInsideBox = faceLeft >= boxLeft && faceRight <= boxRight && faceTop >= boxTop && faceBottom <= boxBottom;

      if (!isInsideBox) {
        setScanMessage('กรุณาให้ใบหน้าอยู่ในกรอบทั้งหมด!');
        setTimeout(() => setScanMessage(null), 3000);
        setIsDetecting(false);
        return;
      }

      const image = captureImage();
      if (!image) {
        setIsDetecting(false);
        return;
      }

      stopCamera();
      setIsScanning(true);
      
      const requestData = {
        face_descriptor: [],
        captured_image: image,
        employee_id: user.employee_id,
        confirmed: true,
        location: wfhMode ? { wfh_mode: true, latitude: 0, longitude: 0 } : location 
      };
      
      const data = await api.recognizeFace(requestData);
      setResult(data);
      
    } catch (error) {
      setResult({ success: false, error: error.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล' });
    } finally {
      setIsDetecting(false);
      setIsScanning(false);
    }
  };

  // เรนเดอร์ UI
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      
      {/* Modal จัดการอุปกรณ์ (ลบเครื่องเก่า) */}
      {showDeviceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in">
            <div className="px-6 py-4 bg-gray-50 border-b flex justify-between items-center">
              <h2 className="text-lg font-bold flex items-center"><Smartphone className="h-5 w-5 mr-2 text-blue-500" /> จัดการอุปกรณ์</h2>
              <button onClick={() => setShowDeviceModal(false)}><X className="h-6 w-6 text-gray-400" /></button>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto space-y-3">
              {devices.length === 0 ? (
                <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <p className="text-sm text-gray-500">ไม่มีอุปกรณ์ที่ลงทะเบียน</p>
                </div>
              ) : (
                devices.map((device, idx) => (
                  <div key={device.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl shadow-sm hover:border-blue-300 transition-all">
                    <div className="flex items-center space-x-4 overflow-hidden">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0 font-bold">{idx + 1}</div>
                      <div className="truncate">
                        <h4 className="font-semibold text-gray-800 text-sm truncate" title={device.device_name || 'ไม่ระบุอุปกรณ์'}>{device.device_name || 'อุปกรณ์ที่ไม่ได้ระบุชื่อ'}</h4>
                        <div className="flex items-center text-xs text-gray-500 mt-1"><Calendar className="h-3 w-3 mr-1" />{new Date(device.created_at).toLocaleDateString('th-TH')}</div>
                      </div>
                    </div>
                    <button onClick={() => handleDeleteDevice(device.id, device.device_name || `อุปกรณ์ที่ ${idx + 1}`)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0 ml-2" title="ลบอุปกรณ์นี้"><Trash2 className="h-5 w-5" /></button>
                  </div>
                ))
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t">
              <button onClick={() => setShowDeviceModal(false)} className="w-full py-3 bg-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-300 transition-colors">ปิดหน้าต่าง</button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        
        {/* Info Header */}
        <div className={`grid ${isCameraMode ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
          {isCameraMode && (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center shadow-lg border border-white/20">
              <Shield className={`h-6 w-6 mx-auto mb-2 ${modelsLoaded ? 'text-green-500' : 'text-gray-400'}`} />
              <div className="text-xs font-medium text-gray-600">AI Model</div>
              <div className={`text-sm font-bold ${modelsLoaded ? 'text-green-600' : 'text-gray-500'}`}>{modelsLoaded ? 'Ready' : 'Loading...'}</div>
            </div>
          )}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center shadow-lg border border-white/20">
            <MapPin className={`h-6 w-6 mx-auto mb-2 ${wfhMode ? 'text-indigo-500' : (location && !location.loading && !location.error ? 'text-blue-500' : 'text-gray-400')}`} />
            <div className="text-xs font-medium text-gray-600">{wfhMode ? 'WFH Mode' : 'Location'}</div>
            <div className={`text-sm font-bold ${wfhMode ? 'text-indigo-600' : (location && !location.loading && !location.error ? 'text-blue-600' : 'text-gray-500')}`}>
              {wfhMode ? 'Active' : (location && location.loading ? 'Finding...' : location && !location.error ? 'Ready' : 'Waiting')}
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center shadow-lg border border-white/20">
            <Clock className="h-6 w-6 mx-auto mb-2 text-indigo-500" />
            <div className="text-xs font-medium text-gray-600">Time</div>
            <div className="text-sm font-bold text-indigo-600">
              {currentTime.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
          </div>
        </div>

        {/* Main Card (Passkey OR Camera Fallback) */}
        {!result && (
          <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 overflow-hidden relative">
            
            {/* กรณี 1: ใช้งาน Camera Mode (WFH หรือ กดสำรอง) */}
            {isCameraMode ? (
              <div className="relative bg-black rounded-t-3xl overflow-hidden" style={{ aspectRatio: '1/1' }}>
                <video
                  ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover"
                  style={{ display: cameraActive ? 'block' : 'none', transform: 'scaleX(-1)', objectPosition: 'center top' }}
                  onLoadedMetadata={() => {
                    if (videoRef.current) {
                      videoRef.current.play();
                      setReadyCountdown(3);
                      if(modelsLoaded) faceapi.detectAllFaces(videoRef.current, new faceapi.TinyFaceDetectorOptions()).catch(()=>{});
                    }
                  }}
                />
                <canvas ref={captureCanvasRef} style={{ display: 'none' }} />
                
                {!cameraActive && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white">
                    <div className="text-center"><Camera className="h-12 w-12 mx-auto mb-4 animate-pulse text-white/70" />
                    <h3 className="font-bold">กำลังเปิดระบบกล้อง...</h3></div>
                  </div>
                )}

                {cameraActive && (
                  <div className="absolute inset-0">
                    <div className="absolute inset-0 flex flex-col items-center pt-10 pointer-events-none">
                      <div ref={boxRef} className="w-[45%] h-[65%] border-2 border-white/50 rounded-3xl relative mb-4">
                        <div className="absolute -top-1 -left-1 w-6 h-6 border-l-4 border-t-4 border-green-400 rounded-tl-lg"></div>
                        <div className="absolute -top-1 -right-1 w-6 h-6 border-r-4 border-t-4 border-green-400 rounded-tr-lg"></div>
                        <div className="absolute -bottom-1 -left-1 w-6 h-6 border-l-4 border-b-4 border-green-400 rounded-bl-lg"></div>
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 border-r-4 border-b-4 border-green-400 rounded-br-lg"></div>
                      </div>
                      {scanMessage && <div className="bg-red-500/80 text-white px-4 py-2 rounded-full font-medium text-sm shadow-lg">{scanMessage}</div>}
                    </div>
                    
                    <div className="absolute bottom-0 left-0 right-0 flex justify-center z-10">
                      <button
                        onClick={handleManualCapture} disabled={readyCountdown > 0 || isDetecting || isScanning}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-6 rounded-none shadow-[0_-5px_15px_rgba(0,0,0,0.2)] flex items-center justify-center space-x-2 disabled:bg-gray-600"
                      >
                        {isScanning ? <span>กำลังบันทึก...</span> :
                         readyCountdown > 0 ? <><div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div><span className="text-lg">เตรียมระบบ... {readyCountdown}</span></> :
                         isDetecting ? <><div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div><span className="text-lg">กำลังตรวจสอบ...</span></> : <><Camera className="w-6 h-6" /><span className="text-lg">ลงเวลา</span></>}
                      </button>
                    </div>
                  </div>
                )}

                {/* ปุ่มกลับไปพาสคีย์ (ถ้าไม่ได้อยู่โหมด WFH) */}
                {!wfhMode && (
                  <button onClick={() => setUseFallbackCamera(false)} className="absolute top-4 right-4 bg-black/50 text-white p-2 rounded-full z-20">
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            ) 
            
            /* กรณี 2: โควต้า Passkey เต็ม */
            : isWrongDevice ? (
              <div className="p-8 text-center">
                <div className="w-28 h-28 bg-red-100 text-red-600 rounded-full flex items-center justify-center shadow-inner mx-auto mb-6"><AlertCircle className="h-16 w-16" /></div>
                <h3 className="text-2xl font-extrabold text-red-600 mb-2">{isBoundToAnotherUser ? 'อุปกรณ์ไม่ถูกต้อง' : 'โควต้าอุปกรณ์เต็ม'}</h3>
                <p className="text-gray-500 mb-8 text-sm">{isBoundToAnotherUser ? 'กรุณาใช้อุปกรณ์ที่ลงทะเบียนไว้' : 'กรุณาลบเครื่องเก่าเพื่อใช้งานเครื่องนี้'}</p>
                {isBoundToAnotherUser ? (
                  <button disabled className="w-[85%] bg-gray-300 text-gray-500 font-bold py-4 rounded-2xl cursor-not-allowed">ไม่สามารถลงเวลาได้</button>
                ) : (
                  <button onClick={() => setShowDeviceModal(true)} className="w-[85%] bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-2xl shadow-xl">จัดการอุปกรณ์</button>
                )}
              </div>
            ) 
            
            /* กรณี 3: ยังไม่ลงทะเบียน Passkey */
            : !isRegistered ? (
              <div className="p-8 text-center">
                <div className="w-28 h-28 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shadow-inner mx-auto mb-6"><Fingerprint className="h-16 w-16" /></div>
                <h3 className="text-2xl font-extrabold text-gray-800 mb-2">ลงทะเบียนไบโอเมทริกซ์</h3>
                <p className="text-gray-500 mb-8 text-sm">กรุณาลงทะเบียนลายนิ้วมือ/ใบหน้าก่อนเริ่มใช้งาน</p>
                <button onClick={handleRegisterDevice} disabled={isScanning} className="w-full bg-blue-600 text-white font-bold py-4 rounded-2xl shadow-xl">
                  {isScanning ? 'กำลังดำเนินการ...' : 'ลงทะเบียนอุปกรณ์นี้'}
                </button>
              </div>
            ) 
            
            /* กรณี 4: รอสแกน Passkey (สำหรับโหมดปกติ) */
            : (
              <div className="p-8 text-center">
                <div className="w-28 h-28 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shadow-inner mx-auto mb-6">
                  <Fingerprint className={`h-16 w-16 ${isScanning ? 'animate-pulse' : ''}`} />
                </div>
                <h3 className="text-2xl font-extrabold text-gray-800 mb-2">ระบบลงเวลาทำงาน</h3>
                <p className="text-gray-500 mb-8 text-sm">
                  {isScanning ? 'กำลังพยายามเรียกใช้งานพาสคีย์อัตโนมัติ...' : 'กดปุ่มด้านล่างเพื่อยืนยันตัวตน'}
                </p>
                <button
                  onClick={() => handleWebAuthnScan(false)}
                  disabled={isScanning || !isWfhChecked || (!wfhMode && location?.loading)}
                  className="w-[85%] bg-blue-600 text-white font-bold py-4 rounded-2xl shadow-xl mb-4 disabled:opacity-50"
                >
                  {isScanning ? 'กำลังตรวจสอบ...' : 'กดเพื่อสแกนพาสคีย์'}
                </button>
                
                <button onClick={() => setUseFallbackCamera(true)} className="text-sm text-blue-500 underline flex items-center justify-center w-full mt-2">
                  <Camera className="h-4 w-4 mr-1" /> พาสคีย์ใช้ไม่ได้? ใช้กล้องสแกนใบหน้าแทน
                </button>
              </div>
            )}
          </div>
        )}

        <div className="space-y-2">
            {/* ซ่อนปุ่มรีเฟรชเมื่อมี result ออกมาแล้ว */}
            {!result && !wfhMode && (
              <button
                onClick={loadCurrentLocation}
                disabled={location?.loading}
                className={`w-full ${location?.loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-orange-500 hover:shadow-xl hover:scale-105'} text-white py-3 px-6 rounded-2xl font-bold shadow-lg transform transition-all duration-200 flex items-center justify-center space-x-2`}
              >
                <MapPin className={`h-5 w-5 ${location?.loading ? 'animate-pulse' : ''}`} />
                <span>{location?.loading ? 'กำลังอัปเดตพิกัด...' : 'รีเฟรชตำแหน่ง'}</span>
              </button>
            )}

            {/* แสดงผลลัพธ์และปุ่มกลับสู่หน้าสแกน */}
            {result && (
              <div className="space-y-4 animate-in slide-in-from-bottom-4 duration-300">
                <div className={`p-6 rounded-2xl ${result.success ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                  <div className={`flex flex-col items-center justify-center text-center ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                    <div className="flex items-center justify-center space-x-3 w-full">
                      {result.success ? (
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                          <CheckCircle className="h-7 w-7 text-white" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                          <AlertCircle className="h-7 w-7 text-white" />
                        </div>
                      )}
                      <p className="text-xl font-bold">{result.message || result.error}</p>
                    </div>
                    
                    <div className="w-full mt-4">
                      {result.success && result.employee && (
                        <div className="space-y-1.5 flex flex-col items-center">
                          <div className="flex items-center justify-center space-x-2">
                            <Users className="h-4 w-4" />
                            <span className="font-medium">{result.employee.first_name} {result.employee.last_name}</span>
                          </div>
                          <div className="flex items-center justify-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${result.action === 'check_in' ? 'bg-blue-500' : 'bg-orange-500'}`}></div>
                            <span className="text-sm">{result.action === 'check_in' ? 'เข้างาน' : 'ออกงาน'}</span>
                          </div>
                          <div className="flex items-center justify-center space-x-2">
                            <Clock className="h-4 w-4" />
                            <span className="text-sm">{new Date().toLocaleString('th-TH')}</span>
                          </div>
                        </div>
                      )}

                      {!result.success && (
                        <div className="text-sm flex flex-col items-center text-red-700/80 space-y-1">
                          {result.distance && (
                            <p>ระยะห่าง: {result.distance} (สูงสุด: {result.max_distance})</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    setResult(null);
                    setIsScanning(false); 
                    if (isCameraMode) startCamera();
                    else setHasAutoTriggered(false); 
                  }}
                  className="w-full bg-gray-800 text-white font-bold py-4 rounded-2xl hover:bg-gray-700 transition-colors"
                >
                  ปิดหน้าต่างนี้ / กลับไปหน้าสแกน
                </button>
              </div>
            )}
        </div>
        
        {/* แถบแสดง Location และ WFH ด้านล่าง */}
        {!result && !wfhMode && location && !location.loading && !location.error && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
            <div className="flex items-center space-x-3 text-sm">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-blue-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">Current Location</p>
                <p className="text-gray-600 truncate">
                  {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  {location.accuracy && (
                    <span className="text-gray-500"> (±{Math.round(location.accuracy)}m)</span>
                  )}
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className={`w-3 h-3 rounded-full ${
                  location.accuracy <= 50 ? 'bg-green-500' : 
                  location.accuracy <= 100 ? 'bg-yellow-500' : 
                  'bg-red-500'
                }`}></div>
              </div>
            </div>
            {location.accuracy > 50 && (
              <div className="mt-3 p-3 bg-yellow-50 rounded-xl border border-yellow-200">
                <p className="text-xs text-yellow-700 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {location.accuracy > 100 ? 
                    'ความแม่นยำของตำแหน่งต่ำ กรุณาไปที่บริเวณโล่ง' : 
                    'ความแม่นยำปานกลาง แนะนำให้ไปที่บริเวณโล่ง'}
                </p>
              </div>
            )}
          </div>
        )}

        {!result && wfhMode && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-4 shadow-lg border border-indigo-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
                <MapPin className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-indigo-900">โหมด Work From Home</p>
                <p className="text-sm text-indigo-700">ข้ามการตรวจสอบตำแหน่ง GPS</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FaceScanner;